import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { CtaLabel, ChevronIcon } from '@/components/generals/cta'
import { NativeLogo } from '@/components/generals/native-logo'

import {
    primaryLinks,
    mobileLinks,
    socialLinks,
    legalLinks
} from '@/constants/site'

type Gsap = typeof import('gsap').gsap
type Timeline = ReturnType<Gsap['timeline']>

// Switches the bar to its light skin whenever it sits over a light section, exactly like the original Navbar script.
const useNavbarGround = (): void => {
    useEffect(() => {
        const navbar = document.querySelector<HTMLElement>('.navbar')
        const grounds = Array.from(document.querySelectorAll('[data-ground="light"]'))

        if (!navbar || !grounds.length) return

        const update = (): void => {
            const rect = navbar.getBoundingClientRect()
            const middle = rect.top + rect.height / 2
            const below = rect.bottom + 1
            const light = grounds.some((ground) => {
                const box = ground.getBoundingClientRect()

                return (box.top <= middle && box.bottom > middle) || (box.top <= below && box.bottom > below)
            })

            navbar.classList.toggle('navbar-light', light)
            document.body.dataset.navTheme = light ? 'light' : 'dark'
        }

        update()

        const observer = new ResizeObserver(update)

        window.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        observer.observe(document.body)

        return () => {
            window.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
            observer.disconnect()
            delete document.body.dataset.navTheme
        }
    }, [])
}

// The sliding underlay menu for small screens, ported from the original NavMenu script.
const useUnderlayMenu = (): void => {
    useEffect(() => {
        const mobile = window.matchMedia('(max-width: 900px)')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
        const cleanups: (() => void)[] = []

        let started = false
        let plain = false
        let disposed = false

        const elements = () => ({
            toggle: document.querySelector<HTMLButtonElement>('[data-underlay-nav-toggle]'),
            labels: document.querySelectorAll('.underlay-nav__toggle-label'),
            bars: document.querySelectorAll('.underlay-nav__toggle-bar'),
            menu: document.querySelector<HTMLElement>('[data-underlay-nav-menu]'),
            large: document.querySelectorAll('[data-reveal-l]'),
            small: document.querySelectorAll('[data-reveal-s]'),
            border: document.querySelector('.underlay-nav__bottom-border'),
            main: document.querySelector<HTMLElement>('[data-main]'),
            overlay: document.querySelector<HTMLElement>('[data-underlay-nav-overlay]'),
            dark: document.querySelector('.underlay-nav__dark'),
            corners: document.querySelectorAll('.underlay-nav__corner'),
            rows: document.querySelectorAll('.underlay-nav__border-row')
        })

        const animated = (gsap: Gsap): void => {
            const { toggle, labels, bars, menu, large, small, border, main, overlay, dark, corners, rows } = elements()

            if (!toggle || !menu || !main || !overlay) return

            let open = false
            let timeline: Timeline
            let openEnd = 0
            let resizeTimer = 0

            const offset = (): number => -menu.offsetWidth

            gsap.set(overlay, { visibility: 'hidden', pointerEvents: 'none' })
            gsap.set(dark, { autoAlpha: 0 })
            gsap.set(main, { x: 0 })
            gsap.set(labels, { yPercent: 0 })
            gsap.set(bars, { y: 0, rotation: 0 })
            gsap.set(border, { scaleX: 0 })
            gsap.set(rows[0], { yPercent: -100 })
            gsap.set(rows[1], { yPercent: 100 })
            gsap.set(corners, { scale: 0 })

            timeline = gsap.timeline({ paused: true, defaults: { ease: 'energy', easeReverse: 'power2.inOut' } })
            timeline.set(overlay, { visibility: 'visible', pointerEvents: 'auto' }, 0)
            timeline
                .to([main, overlay], { x: offset, duration: 0.7 }, 0)
                .to(dark, { autoAlpha: 1, duration: 0.5 }, 0)
                .to(corners, { scale: 1, duration: 0.5 }, 0)
                .to(rows, { yPercent: 0, duration: 0.5 }, 0)
                .to(labels, { yPercent: -100, duration: 0.4 }, 0)
                .to(bars[0], { y: '0.25em', rotation: 45, duration: 0.35, ease: 'back.out(1.4)', easeReverse: 'power3.out' }, 0.05)
                .to(bars[1], { y: '-0.25em', rotation: -45, duration: 0.35, ease: 'back.out(1.4)', easeReverse: 'power3.out' }, 0.05)
                .fromTo(large, { autoAlpha: 0, xPercent: 25 }, { autoAlpha: 1, xPercent: 0, duration: 0.7, stagger: 0.05 }, 0)
                .fromTo(small, { autoAlpha: 0, yPercent: 100 }, { autoAlpha: 1, yPercent: 0, duration: 0.5, stagger: 0.03, ease: 'power3.out' }, 0.3)
                .to(border, { scaleX: 1, duration: 0.5 }, '<')

            openEnd = timeline.duration()
            timeline.addPause()
            timeline
                .to([large, small], { autoAlpha: 0, duration: 0.3 }, '<')
                .to([main, overlay], { x: 0, duration: 0.6 }, '<')
                .to(dark, { autoAlpha: 0, duration: 0.35, ease: 'power2.inOut' }, '<')
                .to(corners, { scale: 0, duration: 0.5 }, '<')
                .to(rows[0], { yPercent: -100, duration: 0.5 }, '<')
                .to(rows[1], { yPercent: 100, duration: 0.5 }, '<')
                .to(labels, { yPercent: 0, duration: 0.25, ease: 'power3.in' }, '<+=0.1')
                .to(bars, { y: 0, rotation: 0, duration: 0.25, ease: 'power3.in' }, '<')
                .set(overlay, { visibility: 'hidden', pointerEvents: 'none' })

            const flip = (): void => {
                open = !open
                toggle.setAttribute('aria-expanded', String(open))
                toggle.setAttribute('aria-label', open ? 'close menu' : 'open menu')
                document.body.setAttribute('data-menu-status', open ? 'open' : '')

                const speed = reduced.matches ? 50 : 1

                if (open) {
                    timeline.invalidate()

                    if (timeline.time() >= openEnd) timeline.timeScale(speed).restart()
                    else timeline.timeScale(speed).play()
                }
                else if (timeline.time() < openEnd) timeline.timeScale(speed).reverse()
                else timeline.timeScale(speed).play()
            }

            const onOverlay = (): void => {
                if (open) flip()
            }

            const onKey = (event: KeyboardEvent): void => {
                if (event.key === 'Escape' && open) {
                    flip()
                    toggle.focus()
                }
            }

            const onBreakpoint = (event: MediaQueryListEvent): void => {
                if (!event.matches && open) flip()
            }

            const onResize = (): void => {
                window.clearTimeout(resizeTimer)
                resizeTimer = window.setTimeout(() => {
                    if (open) gsap.set([main, overlay], { x: offset() })
                    else timeline.invalidate()
                }, 150)
            }

            toggle.addEventListener('click', flip)
            overlay.addEventListener('click', onOverlay)
            document.addEventListener('keydown', onKey)
            mobile.addEventListener('change', onBreakpoint)
            window.addEventListener('resize', onResize)

            cleanups.push(() => {
                toggle.removeEventListener('click', flip)
                overlay.removeEventListener('click', onOverlay)
                document.removeEventListener('keydown', onKey)
                mobile.removeEventListener('change', onBreakpoint)
                window.removeEventListener('resize', onResize)
                window.clearTimeout(resizeTimer)
                timeline.kill()
                document.body.removeAttribute('data-menu-status')
            })
        }

        const fallback = (): void => {
            const { toggle, overlay } = elements()

            if (plain || !toggle) return

            plain = true

            const root = document.documentElement

            const setOpen = (open: boolean): void => {
                root.classList.toggle('nav-plain-open', open)
                toggle.setAttribute('aria-expanded', String(open))
                toggle.setAttribute('aria-label', open ? 'close menu' : 'open menu')
            }

            const onToggle = (): void => setOpen(!root.classList.contains('nav-plain-open'))
            const onOverlay = (): void => setOpen(false)
            const onKey = (event: KeyboardEvent): void => {
                if (event.key === 'Escape') setOpen(false)
            }
            const onBreakpoint = (event: MediaQueryListEvent): void => {
                if (!event.matches) setOpen(false)
            }

            toggle.addEventListener('click', onToggle)
            overlay?.addEventListener('click', onOverlay)
            document.addEventListener('keydown', onKey)
            mobile.addEventListener('change', onBreakpoint)

            cleanups.push(() => {
                toggle.removeEventListener('click', onToggle)
                overlay?.removeEventListener('click', onOverlay)
                document.removeEventListener('keydown', onKey)
                mobile.removeEventListener('change', onBreakpoint)
                root.classList.remove('nav-plain-open')
            })
        }

        const start = async (): Promise<void> => {
            if (started || !mobile.matches) return

            started = true

            try {
                const [{ gsap }, { CustomEase }] = await Promise.all([import('gsap'), import('gsap/CustomEase')])

                if (disposed) return

                gsap.registerPlugin(CustomEase)
                CustomEase.create('energy', 'M0,0 C0.32,0.72 0,1 1,1')
                animated(gsap)
            }
            catch (error) {
                console.warn('[nav] motion library unavailable, using the plain menu', error)
                fallback()
            }
        }

        start()
        mobile.addEventListener('change', start)

        return () => {
            disposed = true
            mobile.removeEventListener('change', start)
            cleanups.forEach((cleanup) => cleanup())
        }
    }, [])
}

export const Navbar: FC = (): ReactNode => {
    useNavbarGround()
    useUnderlayMenu()

    return (
        <>
            <header className='navbar'>
                <div className='navbar-inner'>
                    <nav className='navbar-nav' aria-label='Primary'>
                        <a className='navbar-logo' href='/' aria-label='Native, home'>
                            <NativeLogo className='navbar-logo-svg' idPrefix='navbar' />
                        </a>

                        <ul className='navbar-links'>
                            {primaryLinks.map((link) => (
                                <li key={link.href}>
                                    <a className='navbar-link' href={link.href}>
                                        {link.label}
                                        <img className='navbar-link-icon' src='/images/ui/nav-plus.svg' alt='' width={8} height={8} />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <a className='navbar-cta' href='/contact'>
                        <CtaLabel text='Work with us' />
                        <ChevronIcon />
                    </a>

                    <button data-underlay-nav-toggle='' aria-expanded='false' aria-label='open menu' className='underlay-nav__toggle'>
                        <span className='underlay-nav__toggle-text'>
                            <span className='underlay-nav__toggle-label'>Menu</span>
                            <span className='underlay-nav__toggle-label'>Close</span>
                        </span>
                        <span className='underlay-nav__toggle-icon'>
                            <span className='underlay-nav__toggle-bar' />
                            <span className='underlay-nav__toggle-bar' />
                        </span>
                    </button>
                </div>
            </header>

            <div className='underlay-nav'>
                <nav data-underlay-nav-menu='' className='underlay-nav__menu' aria-label='Mobile'>
                    <div className='underlay-nav__inner'>
                        <ul className='underlay-nav__list'>
                            {mobileLinks.map((link) => (
                                <li key={link.href} data-reveal-l=''>
                                    <a href={link.href} className='underlay-nav__link-large'>
                                        <span className='underlay-nav__link-label'>{link.label}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <div className='underlay-nav__bottom'>
                            <div className='underlay-nav__bottom-col'>
                                <div data-reveal-s=''>
                                    <span className='underlay-nav__link-small is--faded'>Socials</span>
                                </div>
                                <ul className='underlay-nav__list is--small'>
                                    {socialLinks.map((link) => (
                                        <li key={link.href} data-reveal-s=''>
                                            <a href={link.href} className='underlay-nav__link-small' target='_blank' rel='noopener'>
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className='underlay-nav__bottom-col'>
                                <div data-reveal-s=''>
                                    <span className='underlay-nav__link-small is--faded'>Quick Links</span>
                                </div>
                                <ul className='underlay-nav__list is--small'>
                                    {legalLinks.map((link) => (
                                        <li key={link.href} data-reveal-s=''>
                                            <a href={link.href} className='underlay-nav__link-small'>
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className='underlay-nav__bottom-border' />
                        </div>
                    </div>
                </nav>

                <div data-underlay-nav-overlay='' className='underlay-nav__overlay'>
                    <div className='underlay-nav__dark' />
                    <div className='underlay-nav__borders'>
                        <div className='underlay-nav__border-row'>
                            <div className='underlay-nav__border' />
                            <div className='underlay-nav__corner' />
                        </div>
                        <div className='underlay-nav__border-row'>
                            <div className='underlay-nav__corner is--bottom' />
                            <div className='underlay-nav__border' />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
